import { sendToAnalyticsCustomEventObj } from 'lib/googleAnalytics';
import { includesSequence } from 'lib/helpers';

const deviceWrappers = [
  require('./wrappers/Keon').default,
  require('./wrappers/KeonWiFi').default,
  require('./wrappers/Keon2').default,
  require('./wrappers/Onyx').default,
  require('./wrappers/Onyx2').default,
  require('./wrappers/OnyxUltra').default,
  require('./wrappers/PulseInteractive').default,
  require('./wrappers/PlayBulb').default,
  require('./wrappers/PowerBlow').default,
  require('./wrappers/Launch').default,
  require('./wrappers/SvakomSam').default,
  require('./wrappers/SvakomSamNeo2').default,
  require('./wrappers/PleasureDrive').default,
  require('./wrappers/SvakomAlex').default,
  require('./wrappers/SvakomAlex2').default,
  require('./wrappers/Handy.js').default,
  require('./wrappers/HandyV4.js').default,
  require('./wrappers/Titan11').default,
  require('./wrappers/AutoblowUltra').default,
  require('./wrappers/UnsupportedDevice').default,
  require('./wrappers/PoweShot').default,
];
const RenamedPleasureDriveWrapper = require('./wrappers/PleasureDrive').default;
const RenamedKeon2Wrapper = require('./wrappers/Keon2').default;
const UnsupportedDeviceWrapper = require('./wrappers/UnsupportedDevice').default;

const DEVICE_INFORMATION_SERVICE = 0x180a;
const DEVICE_SPECIFIC_INFO = 0x2a29; // Manufacturer Name String

// Keon2 keeps its Wi-Fi provisioning characteristic in the primary service.
// The other 0x1400 toys (PowerBlow, PowerShot) do not have it, so the pair
// identifies a Keon2 whatever name it advertises under.
const KEON2_PRIMARY_SERVICE = 0x1400;
const KEON2_WIFI_PROV_CONTROL = 0x1901;

const RENAMED_DEVICES_SERVICES = [
  KEON2_PRIMARY_SERVICE,
  DEVICE_INFORMATION_SERVICE,
  0x180f,
  0x1900,
  0x1600,
  0xffe5,
  0xff90,
];

const PLEASURE_DRIVE_NAME_MANUFACTURER_DATA = [82, 70, 32, 67, 114, 97, 122, 121, 0, 0];

/**
 * Read the Manufacturer Name String of a connected device.
 * Not every toy exposes the Device Information service, so a miss is expected
 * and reported as an empty array rather than thrown.
 */
async function readManufacturerName(server) {
  try {
    const service = await server.getPrimaryService(DEVICE_INFORMATION_SERVICE);
    const characteristic = await service.getCharacteristic(DEVICE_SPECIFIC_INFO);
    const value = await characteristic.readValue();
    const manufacturerDataArr = [];
    for (let i = 0; i < value.byteLength; i++) {
      manufacturerDataArr.push(value.getUint8(i));
    }
    return manufacturerDataArr;
  } catch (error) {
    return [];
  }
}

/**
 * Recognise a Keon2 by its GATT layout instead of by its advertised name.
 */
async function hasKeon2Signature(server) {
  try {
    const service = await server.getPrimaryService(KEON2_PRIMARY_SERVICE);
    await service.getCharacteristic(KEON2_WIFI_PROV_CONTROL);
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Get array of all possible device names
 */
export function getAllDeviceNames() {
  return deviceWrappers.reduce((names, wrapper) => names.concat(wrapper.deviceNames), []);
}

export async function createDevice(webBleDevice, stateCallback) {
  const deviceName = webBleDevice.name;
  let result = null;
  deviceWrappers.forEach((Wrapper) => {
    if (Wrapper.deviceNames.includes(deviceName)) {
      result = new Wrapper(webBleDevice);
    }
  });

  if (!result) {
    // The device advertises a name we don't know, so identify it over GATT.
    const server = await webBleDevice.gatt.connect();
    const manufacturerName = await readManufacturerName(server);

    if (includesSequence(manufacturerName, PLEASURE_DRIVE_NAME_MANUFACTURER_DATA)) {
      result = new RenamedPleasureDriveWrapper(webBleDevice);
    } else if (await hasKeon2Signature(server)) {
      result = new RenamedKeon2Wrapper(webBleDevice);
    } else {
      // Let the caller report it instead of failing the whole discovery.
      result = new UnsupportedDeviceWrapper(webBleDevice);
    }
  }

  result.connectionStateCallback = stateCallback;
  return result;
}

/**
 * Connect a bluetooth device wrapper.
 * Device wrapper is a class around BluetoothDevice.
 * https://developer.mozilla.org/en-US/docs/Web/API/BluetoothDevice
 * The wrapper cannot exist without BluetoothDevice and cannot be created without
 * actual connection to the bluetooth device.
 * @return a promise with is resolved with the device wrapper
 */
export const discoverDevice = async (stateCallback) => {
  const allDevices = getAllDeviceNames().map((name) => ({ name }));

  const allServices = deviceWrappers.reduce(
    (services, wrapper) => services.concat(wrapper.services),
    [],
  );

  allServices.push(...RENAMED_DEVICES_SERVICES);
  const options = {
    filters: [
      ...allDevices,
      {
        manufacturerData: [
          {
            companyIdentifier: 1445, // Kiiroo company (0x05A5)
          },
        ],
      },
      {
        manufacturerData: [
          {
            companyIdentifier: 1040, // Fender musical instrument company (0x0410)
          },
        ],
      },
      {
        manufacturerData: [
          {
            companyIdentifier: 277, // e.solutions company (0x0115)
          },
        ],
      },
    ],
    optionalServices: allServices,
  };

  let device = null;
  try {
    device = await navigator.bluetooth.requestDevice(options);
  } catch (error) {
    if (error.toString().includes('User cancelled the requestDevice() chooser.')) {
      sendToAnalyticsCustomEventObj('user_cancelled_chooser');
    } else if (error.toString().includes('Bluetooth adapter not available.')) {
      sendToAnalyticsCustomEventObj('bluetooth_adapter_not_available', {
        user_agent: navigator.userAgent,
      });
    } else {
      sendToAnalyticsCustomEventObj('popup_connect_device_exception', {
        message: error.toString(),
      });
    }

    // Device selection was probably cancelled
    return null;
  }
  const deviceWrapper = await createDevice(device, stateCallback);
  return deviceWrapper;
};
